/**
 * AgentDebugTokensTab — Tokens & cost tab inside the dev tools panel.
 *
 * Lives next to Tools and Skills. Shows the same information the Insights
 * page surfaces (cost, tokens, cache state, model-switch context) but
 * scoped to the conversation the user is currently in, refreshed live as
 * events stream from the agent.
 *
 * Design:
 *   - Reuses the AgentDebugPanel CSS variables (grays + single blue accent).
 *   - No charts, no animations — read-only data, tightly-packed numbers.
 *   - Refetches summary+timeline whenever the parent's event stream grows
 *     (i.e. when ANY tool event lands — that's our cheap proxy for "the
 *     agent did something, the ledger may have a new row").
 */

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import insightsService, {
  CacheStateLabel,
  ConversationSummaryResponse,
  ConversationTimelineResponse,
  ConversationTurn,
} from '@modules/insights/services/insightsService';

interface AgentDebugTokensTabProps {
  /** UUID of the conversation currently open in the chat. */
  conversationId: string | null;
  /** A bumping integer/counter from the parent — refetch whenever it changes. */
  refetchSignal: number;
  /** Tenant id for the API call's tenant_id param. */
  tenantId?: string;
}

// Map cache states to short, neutral labels.
// No emojis, no bright colors — matches the panel's clean-monochrome aesthetic.
const CACHE_LABEL_KEY: Record<Exclude<CacheStateLabel, null>, string> = {
  cold_start: 'agentDebug.cacheState.coldStart',
  cache_hit: 'agentDebug.cacheState.cacheHit',
  cache_lost_switch: 'agentDebug.cacheState.cacheLostSwitch',
  cache_warming: 'agentDebug.cacheState.cacheWarming',
  cache_rewrite: 'agentDebug.cacheState.cacheRewrite',
  cache_expired: 'agentDebug.cacheState.cacheExpired',
  no_cache: 'agentDebug.cacheState.noCache',
};

// Subtle inline-text color hint per state. Only used for the cache-state
// chip; never for backgrounds, never for the cost numbers themselves.
const CACHE_COLOR: Record<Exclude<CacheStateLabel, null>, string> = {
  cold_start: 'var(--text-tertiary, #9ca3af)',
  cache_hit: 'var(--success-text, #047857)',
  cache_lost_switch: 'var(--danger-text, #b91c1c)',
  cache_warming: 'var(--warning-text, #92400e)',
  cache_rewrite: 'var(--warning-text, #92400e)',
  cache_expired: 'var(--danger-text, #b91c1c)',
  no_cache: 'var(--text-tertiary, #9ca3af)',
};

function fmtN(n: number): string {
  if (n == null) return '—';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('en-US');
}

function fmtUsd(n: number): string {
  if (n == null) return '—';
  if (n === 0) return '$0.000000';
  // Never round cost values — always 6 decimals (the precision the bill is
  // computed at), thousands separators on the integer side for readability.
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const [whole, decimals] = abs.toFixed(6).split('.');
  return `${sign}$${Number(whole).toLocaleString('en-US')}.${decimals}`;
}

export function AgentDebugTokensTab({
  conversationId,
  refetchSignal,
  tenantId,
}: AgentDebugTokensTabProps) {
  const { t } = useLanguage();
  const [summary, setSummary] = useState<ConversationSummaryResponse | null>(null);
  const [timeline, setTimeline] = useState<ConversationTimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Stale-cancel guard for overlapping fetches as new events stream in.
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (!conversationId) {
      setSummary(null);
      setTimeline(null);
      return;
    }
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    Promise.all([
      insightsService.getConversationSummary(conversationId, { tenantId }),
      insightsService.getConversationTimeline(conversationId, { tenantId }),
    ])
      .then(([s, t]) => {
        if (reqIdRef.current !== reqId) return; // stale
        setSummary(s);
        setTimeline(t);
      })
      .catch((err) => {
        if (reqIdRef.current !== reqId) return;
        setError(err?.response?.data?.error ?? t('agentDebug.failedToLoad'));
      })
      .finally(() => {
        if (reqIdRef.current !== reqId) return;
        setLoading(false);
      });
  }, [conversationId, refetchSignal, tenantId]);

  if (!conversationId) {
    return (
      <div className="agent-debug-empty">
        {t('chat.openAConversationToSee')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="agent-debug-empty" style={{ color: 'var(--danger-text, #b91c1c)' }}>
        {error}
      </div>
    );
  }

  if (loading && !summary) {
    return <div className="agent-debug-empty">Loading…</div>;
  }

  if (!summary || summary.call_count === 0) {
    return (
      <div className="agent-debug-empty">{t('agentDebug.noTokenEvents')}</div>
    );
  }

  const turns = timeline?.turns ?? [];
  const totalBillable =
    summary.input_tokens
    + summary.cache_read_tokens
    + summary.cache_creation_tokens
    + summary.output_tokens;

  return (
    <div className="tokens-tab">
      {/* ── Headline summary ──────────────────────────────────────── */}
      <section className="tokens-summary">
        <div className="tokens-summary-row">
          <SummaryStat label={t('agentDebug.totalCost')} value={fmtUsd(summary.total_cost_usd)} primary />
          <SummaryStat label={t('agentDebug.totalTokens')} value={fmtN(totalBillable)} primary />
          <SummaryStat label={t('agentDebug.calls')} value={String(summary.call_count)} />
          <SummaryStat
            label={t('agentDebug.cacheSavings')}
            value={`${summary.cache_savings_pct.toFixed(1)}%`}
            hint={summary.cache_savings_pct > 60 ? t('agentDebug.cacheGood') :
                  summary.cache_savings_pct > 20 ? t('agentDebug.cachePartial') : t('agentDebug.cacheLow')}
          />
          <SummaryStat
            label={t('agentDebug.modelSwitches')}
            value={String(summary.model_switch_count)}
            hint={summary.model_switch_count > 0 ? t('agentDebug.cacheResetOnEach') : undefined}
          />
        </div>
        <div className="tokens-summary-breakdown">
          <BreakdownItem label="input (uncached)" value={fmtN(summary.input_tokens)} note="full rate" />
          <BreakdownItem
            label="cache read"
            value={fmtN(summary.cache_read_tokens)}
            note="~10% rate"
            accent={summary.cache_read_tokens > 0 ? 'good' : undefined}
          />
          <BreakdownItem
            label="cache write"
            value={fmtN(summary.cache_creation_tokens)}
            note="~125–200% rate"
            accent={summary.cache_creation_tokens > 0 ? 'warn' : undefined}
          />
          <BreakdownItem label="output" value={fmtN(summary.output_tokens)} note="full output rate" />
          {summary.reasoning_tokens > 0 && (
            <BreakdownItem
              label="reasoning"
              value={fmtN(summary.reasoning_tokens)}
              note="subset of output"
            />
          )}
        </div>
        {summary.models_used.length > 0 && (
          <div className="tokens-models-row">
            <span className="tokens-row-label">{t('insights.timeline.modelsUsed')}</span>
            {summary.models_used.map((m) => (
              <span key={`${m.provider}/${m.model}`} className="tokens-model-chip" dir="auto">
                {m.provider}/{m.model}
              </span>
            ))}
          </div>
        )}
      </section>

      {/* ── Per-turn timeline ─────────────────────────────────────── */}
      <section className="tokens-turns">
        <div className="tokens-section-header">
          Turn-by-turn
          {turns.length > 0 && (
            <span className="agent-debug-tab-count">{turns.length}</span>
          )}
        </div>
        {turns.length === 0 ? (
          <div className="agent-debug-empty" style={{ padding: '12px 16px' }}>
            {t('chat.noAssistantTurnsRecordedYet')}
          </div>
        ) : (
          turns.map((turn, idx) => (
            <TurnRow key={turn.message_id} turn={turn} index={idx} />
          ))
        )}
      </section>
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────────

function SummaryStat({
  label, value, primary, hint,
}: { label: string; value: string; primary?: boolean; hint?: string }) {
  return (
    <div className="tokens-stat">
      <div className="tokens-stat-label">{label}</div>
      <div dir="auto" className={`tokens-stat-value ${primary ? 'is-primary' : ''}`}>{value}</div>
      {/* Always render the hint slot — empty when not provided — so all five
          cards share the same baseline regardless of which have hints. */}
      <div className="tokens-stat-hint">{hint ?? ' '}</div>
    </div>
  );
}

function BreakdownItem({
  label, value, note, accent,
}: { label: string; value: string; note?: string; accent?: 'good' | 'warn' }) {
  return (
    <div className="tokens-breakdown-item">
      <span className="tokens-breakdown-label">{label}</span>
      <span className={`tokens-breakdown-value tokens-accent-${accent ?? 'none'}`}>{value}</span>
      {note && <span className="tokens-breakdown-note">{note}</span>}
    </div>
  );
}

function TurnRow({ turn, index }: { turn: ConversationTurn; index: number }) {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const cacheLabel = turn.cache_state ? t(CACHE_LABEL_KEY[turn.cache_state]) : null;
  const cacheColor = turn.cache_state ? CACHE_COLOR[turn.cache_state] : 'var(--text-tertiary)';
  const isSwitch = turn.cache_state === 'cache_lost_switch';

  return (
    <>
      {isSwitch && (
        <div className="tokens-switch-banner">
          <span className="tokens-switch-arrow">↓</span>
          <span className="tokens-switch-label">{t('agentDebug.modelSwitched')}</span>
          <code>{turn.switched_from ?? '—'}</code>
          <span>→</span>
          <code>{turn.switched_to ?? turn.model_name ?? '—'}</code>
          <span className="tokens-switch-hint">{t('agentDebug.cacheNoTransfer')}</span>
        </div>
      )}
      <div
        className="tokens-turn-row"
        onClick={() => setExpanded((e) => !e)}
        role="button"
        tabIndex={0}
      >
        <span className="tokens-turn-index">{expanded ? '▾' : '▸'} #{index + 1}</span>
        <span className="tokens-turn-model" dir="auto">
          {turn.provider}/{turn.model_name ?? '—'}
        </span>
        {cacheLabel && (
          <span className="tokens-turn-cache" style={{ color: cacheColor }}>
            {cacheLabel}
          </span>
        )}
        <span dir="auto" className="tokens-turn-numbers">
          <span title={t('agentDebug.titleUncachedInput')}>{t('agentDebug.nIn', { count: fmtN(turn.input_tokens) })}</span>
          {turn.cache_read_tokens > 0 && (
            <span title={t('agentDebug.titleCacheRead')} style={{ color: CACHE_COLOR.cache_hit }}>
              {t('agentDebug.nCache', { count: fmtN(turn.cache_read_tokens) })}
            </span>
          )}
          {turn.cache_creation_tokens > 0 && (
            <span title={t('agentDebug.titleCacheWrite')} style={{ color: CACHE_COLOR.cache_warming }}>
              {t('agentDebug.nWritten', { count: fmtN(turn.cache_creation_tokens) })}
            </span>
          )}
          <span title={t('insights.costTimeline.outputPlain')}>{t('agentDebug.nOut', { count: fmtN(turn.output_tokens) })}</span>
          {turn.reasoning_tokens > 0 && (
            <span title={t('agentDebug.titleReasoning')} style={{ color: 'var(--text-tertiary)' }}>
              {t('agentDebug.nReasoning', { count: fmtN(turn.reasoning_tokens) })}
            </span>
          )}
          {turn.iteration_count > 1 && (
            <span style={{ color: 'var(--text-tertiary)' }}>
              {t('agentDebug.nIter', { count: turn.iteration_count })}
            </span>
          )}
        </span>
        <span className="tokens-turn-cost">{fmtUsd(turn.total_cost_usd)}</span>
      </div>
      {expanded && (
        <div className="tokens-turn-expanded">
          {turn.cache_explanation && (
            <div className="tokens-turn-explanation">{turn.cache_explanation}</div>
          )}
          {turn.events.length > 0 && (
            <div className="tokens-events-list">
              {turn.events.map((ev) => (
                <div key={ev.event_id} className="tokens-event-row">
                  <span className="tokens-event-iter" dir="auto">
                    {ev.iteration_index != null ? `#${ev.iteration_index}` : '—'}
                  </span>
                  <span className="tokens-event-kind">{ev.call_kind}</span>
                  {ev.tool_name && <span className="tokens-event-tool">{ev.tool_name}</span>}
                  <span className="tokens-event-model" dir="auto">
                    {ev.provider}/{ev.model_name}
                  </span>
                  <span className="tokens-event-numbers">
                    {t('agentDebug.eventNumbers', { in: fmtN(ev.input_tokens), out: fmtN(ev.output_tokens) })}
                    {ev.cache_read_tokens > 0 && (
                      <span style={{ color: CACHE_COLOR.cache_hit }}>
                        {t('agentDebug.evCache', { count: fmtN(ev.cache_read_tokens) })}
                      </span>
                    )}
                    {ev.cache_creation_tokens > 0 && (
                      <span style={{ color: CACHE_COLOR.cache_warming }}>
                        {t('agentDebug.nWritten', { count: fmtN(ev.cache_creation_tokens) })}
                      </span>
                    )}
                  </span>
                  <span className="tokens-event-cost">
                    {fmtUsd(ev.total_cost_usd)}
                    {ev.downstream_cost_usd > 0 && (
                      <span
                        className="tokens-event-downstream"
                        title={t('agentDebug.downstreamTitle', { tokens: fmtN(ev.downstream_input_tokens), cost: fmtUsd(ev.downstream_cost_usd) })}
                      >
                        {t('agentDebug.nDownstream', { cost: fmtUsd(ev.downstream_cost_usd) })}
                      </span>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
